import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HiArrowLeft, HiDotsVertical, HiOutlineSearch } from 'react-icons/hi';
import { ColumnType, CustomSwitch, Button, Input, Table } from '@/components';
import Popover from '@/components/Popover';
import { Icon } from '../../assets/icons';
import { useDebounce } from '@/hooks/useDebounce';
import { EditCompany } from '../company/EditCompany';
import { formatCodeNumber } from '@/utils/formatCodeNumber';
import { useCompanyStore } from '@/store/companyStore';


interface User {
  id: string;
  fullName: string;
  emailAddress: string;
  role: string;
  isActive: boolean;
}

const NonMemberDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Store - chỉ lấy data, không fetch
  const { company, loading, error } = useCompanyStore();

  // Local state
  const [searchUsersInput, setSearchUsersInput] = useState('');
  const [users, setUsers] = useState<User[]>([]);

  // Debounce search term
  const debouncedSearchUsers = useDebounce(searchUsersInput, 500);

  // Redirect nếu không có id - wrapper sẽ handle việc fetch data
  useEffect(() => {
    if (!id) {
      navigate('/company');
      return;
    }
  }, [id, navigate]);

  // Handle key down for search input
  const handleUsersKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Force immediate search on Enter
      setSearchUsersInput(searchUsersInput);
    }
  };


  const handleBack = () => {
    navigate('/company', { replace: true });
  };

  const handleUserToggle = (userId: string, checked: boolean) => {
    setUsers(prev =>
      prev.map(user =>
        user.id === userId ? { ...user, isActive: checked } : user
      )
    );
  };

  // const handleCreateUser = () => {
  //   // Implement create user functionality
  // };

  // const handleInformationChange = () => {
  //   // Implement information change functionality
  // };

  // const handleDisableUser = () => {
  //   // Implement disable user functionality
  // };


  const filteredUsers = users.filter(user =>
    user.fullName.toLowerCase().includes(debouncedSearchUsers.toLowerCase()) ||
    user.emailAddress.toLowerCase().includes(debouncedSearchUsers.toLowerCase()) ||
    user.role.toLowerCase().includes(debouncedSearchUsers.toLowerCase())
  );

  // Dropdown state for user actions (popover style, but simplified for now)

  // Users Table Columns
  const usersColumns: ColumnType<User>[] = [
    {
      title: '氏名',
      dataIndex: 'fullName',
      key: 'fullName',
      width: '30%',
    },
    {
      title: 'メールアドレス',
      dataIndex: 'emailAddress',
      key: 'emailAddress',
      width: '35%',
    },
    {
      title: 'ロール',
      dataIndex: 'role',
      key: 'role',
      width: '20%',
    },
    {
      title: ' ',
      key: 'actions',
      width: '15%',
      render: (_, record) => (
        <div className="flex flex-row items-center justify-between w-full gap-0 px-3">
          <CustomSwitch
            checked={record.isActive}
            onCheckedChange={(checked) => handleUserToggle(record.id, checked)}
            size="sm"
          />
          {/* Popover for user actions, giống CompanyDetail */}
          <div className="relative">
            <Popover
              trigger={
                <Button
                  type="button"
                  buttonType="text"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    padding: '0',
                    color: '#666',
                    minWidth: 'auto'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#333';
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#666';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <HiDotsVertical style={{ fontSize: '16px' }} />
                </Button>
              }
              side="bottom"
              align="end"
            >
              <div className="user-menu-dropdown bg-white shadow-lg rounded-lg border border-gray-200 py-2 min-w-[160px]">
                <div className="user-menu-item px-4 py-2 cursor-pointer whitespace-nowrap hover:bg-gray-100 border-b border-gray-200">情報設定</div>
                <div className="user-menu-item px-4 py-2 cursor-pointer whitespace-nowrap text-red-500 hover:bg-red-50">アカウントを削除</div>
              </div>
              <></>
            </Popover>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="py-4 2xl:py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="flex items-center text-blue-600 text-sm">
          <span className="text-cyan hover:underline cursor-pointer">事業所管理</span>
          <span className="mx-2 text-cyan">›</span>
          <span className="text-grey-600">事業所詳細</span>
        </nav>
        <div className="mt-4">
          <Button
            buttonType="text"
            onClick={handleBack}
            className="flex items-center gap-2 text-2xl font-semibold text-gray-800 hover:text-cyan"
          >
            <HiArrowLeft className="w-5 h-5" /> {company?.name || '-'}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col xl:flex-row xl:space-x-6 space-y-6 xl:space-y-0">
        {/* Left Column */}
        <aside className="flex-shrink-0 w-full xl:w-2/5 max-w-md bg-white rounded-lg shadow p-6 flex flex-col min-h-[600px]">
          <h2 className="text-lg font-bold mb-4">事業所情報</h2>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Icon name="loading" className="animate-spin text-teal-500" width="40" height="40" />
            </div>
          ) : error ? (
            <div className="text-red-500 text-center p-4">
              エラー: {error}
            </div>
          ) : company ? (
            <div className="flex-1">
              <div className="flex justify-between text-sm text-gray-600 py-2 border-b border-gray-200">
                <span className="font-semibold">事業所区分</span>
                <span className="text-gray-800">{company.business_type || '非会員'}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600 py-2 border-b border-gray-200">
                <span className="font-semibold">法人名</span>
                <span className="text-gray-800">{company.name || '-'}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600 py-2 border-b border-gray-200">
                <span className="font-semibold">住所</span>
                <span className="text-gray-800 truncate" title={company.address || '-'}>
                  {company.address || '-'}
                </span>
              </div>
              <div className="flex justify-between text-sm text-gray-600 py-2 border-b border-gray-200">
                <span className="font-semibold">電話番号</span>
                <span className="text-gray-800">{formatCodeNumber(company.phone) || '-'}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600 py-2 border-b border-gray-200">
                <span className="font-semibold">メモ</span>
                <span className="text-gray-800">{company.note || '-'}</span>
              </div>
            </div>
          ) : null}

          <div className="mt-6 flex gap-3">
            <EditCompany id={+(id || 0)} className='flex-1'>
              <Button
                variant="outlined"
                color="default"
                className="w-full !border !border-cyan !text-cyan bg-white hover:!border-cyan hover:!text-cyan"
              >
                情報変更
              </Button>
            </EditCompany>
          </div>
        </aside>

        {/* Right Column - Users */}
        <div className="flex-1 flex flex-col space-y-6">
          <section className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              {/* Left: title + search */}
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <h3 className="text-lg font-semibold whitespace-nowrap">ユーザー一覧</h3>
                <div className="flex-1 min-w-0">
                  <Input
                    placeholder="検索"
                    value={searchUsersInput}
                    onChange={(e) => setSearchUsersInput(e.target.value)}
                    className="w-[70%]"
                    icon={<HiOutlineSearch className="w-5 h-5 text-gray-400" />}
                    onKeyDown={handleUsersKeyDown}
                  />
                </div>
              </div>
              {/* Right: button */}
              <div className="flex items-center space-x-3 ml-4">
                <Button buttonType="primary" color="danger">新規作成</Button>
              </div>
            </div>
            <Table
              columns={usersColumns}
              dataSource={filteredUsers}
              rowKey="id"
              bordered={true}
              size="middle"
              className='visible-scrollbar'
            />
          </section>
        </div>
      </div>
    </div>
  );
};

export default NonMemberDetail;
